import { NodeExtension, CommandFunction, ApplySchemaAttributes, NodeSpecOverride } from "@remirror/core";
import { DOMOutputSpec, Node as ProseMirrorNode } from "prosemirror-model";
import { NodeView, EditorView } from "prosemirror-view";
import { NodeSelection } from "prosemirror-state";
import { createRoot, Root } from "react-dom/client";
import CustomAudioPlayer from "../../../../CustomAudioPlayer";
import { Node as ProsemirrorNode } from 'prosemirror-model';
import { AudioIcon, AudioInfo, AudioMeta, AudioProgress, AudioTitle, AudioWrapper } from "./styles";
import { MediaUploadingPlaceholder } from "../MediaUploadedPlaceholder";
import { AttachedFile } from "../../../../../types/attached-file";

class AudioNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;

  constructor(
    private node: ProseMirrorNode,
    private view: EditorView,
    private getPos: () => number,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    this.dom = document.createElement("div");
    this.dom.className = "remirror-audio-wrapper";
    this.reactContainer = document.createElement("div");
    this.reactContainer.style.width = "100%";
    this.dom.appendChild(this.reactContainer);
    this.render();
    this.addClickSelection();

    // Aplicar bloqueo de descarga si está habilitado y NO es el owner
    if (this.blockFilesDownload && !this.isOwner) {
      this.applyDownloadBlock();
    }

    // Actualizar estado del bloqueo
    this.updateBlockStatus();

  }

  private addClickSelection() {
    this.dom.addEventListener("click", (event) => {
      event.preventDefault()
      event.stopPropagation()

      // Default behavior: select the entire node
      const tr = this.view.state.tr.setSelection(NodeSelection.create(this.view.state.doc, this.getPos()))
      this.view.dispatch(tr)
      this.view.focus()
    })
  }

  private applyDownloadBlock() {
    // Usar los valores de la extensión, no del nodo
    const shouldBlock = this.blockFilesDownload && !this.isOwner;


    // Bloquear botón derecho
    this.dom.addEventListener('contextmenu', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear arrastrar audio
    this.dom.addEventListener('dragstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear selección de texto
    this.dom.addEventListener('selectstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear copiar
    this.dom.addEventListener('copy', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear cortar
    this.dom.addEventListener('cut', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Agregar clase CSS para estilos adicionales solo si NO es owner
    if (!this.isOwner) {
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      // Si es owner, asegurar que NO tenga las clases de bloqueo
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }

  // Método para verificar el estado actual del bloqueo
  private getBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    return {
      blockFilesDownload: this.blockFilesDownload,
      isOwner: this.isOwner,
      finalBlockDownload: shouldBlock,
      shouldBlock,
      hasBlockClasses: this.dom.classList.contains('download-blocked'),
      containerHasBlockClasses: this.reactContainer.classList.contains('download-blocked')
    };
  }

  // Método para actualizar dinámicamente el estado del bloqueo
  private updateBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    if (shouldBlock) {
      // Aplicar bloqueo
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      // Remover bloqueo
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }

  render() {
    const {
      src,
      title,
      isCompressed,
      isOwner,
      blockFilesDownload,
      canBeCompressed,
      optimizationRate,
      "data-uploading": uploading,
      "data-progress": progress,
      filename,
      filesize,
    } = this.node.attrs;

    if (this.root) this.root.unmount();
    this.reactContainer.innerHTML = "";

    const isUploading =
      uploading === true || uploading === "true" || uploading === 1 || uploading === "1";

    this.root = createRoot(this.reactContainer);
    if (isUploading) {
      this.root.render(
        <MediaUploadingPlaceholder
          type="audio"
          filename={filename}
          progress={progress}
        />
      );
    } else {

      this.root.render(
        <CustomAudioPlayer
          audioUrl={src}
          isCompressed={isCompressed}
          isOwner={this.isOwner}
          blockDownload={this.blockFilesDownload}
          canBeCompressed={canBeCompressed}
          optimizationRate={optimizationRate}
          title={title}
        />
      );
    }
  }

  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  destroy() {
    if (this.root) this.root.unmount();
  }
}

class PreviewAudioNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    this.dom = document.createElement("div");
    this.dom.className = "remirror-audio-wrapper";
    this.reactContainer = document.createElement("div");
    this.reactContainer.style.borderRadius = "8px";
    this.reactContainer.style.position = "relative";
    this.reactContainer.style.overflow = "hidden";
    this.reactContainer.style.width = "100%";
    this.reactContainer.style.height = "100%";
    this.reactContainer.style.border = "1px solid #e0e0e0";

    this.dom.appendChild(this.reactContainer);
    this.render();

    // Aplicar bloqueo de descarga si está habilitado y NO es el owner
    if (this.blockFilesDownload && !this.isOwner) {
      this.applyDownloadBlock();
    }

    // Actualizar estado del bloqueo
    this.updateBlockStatus();

    // Debug log para verificar la configuración
  }

  render() {
    const {
      src,
      title,
      isCompressed,
      isOwner,
      blockFilesDownload,
      canBeCompressed,
      optimizationRate,
      "data-uploading": uploading,
      "data-progress": progress,
      filename,
      filesize,
      media_file_id
    } = this.node.attrs;

    if (this.root) this.root.unmount();
    this.reactContainer.innerHTML = "";

    const isUploading =
      uploading === true || uploading === "true" || uploading === 1 || uploading === "1";

    this.root = createRoot(this.reactContainer);

    if (isUploading) {
      this.root.render(
        <MediaUploadingPlaceholder
          type="audio"
          filename={filename}
          progress={progress}
        />
      );
    } else {
      const finalSrc = this.mediaFilesMap?.[this.node.attrs.media_file_id]?.signed_url || src;


      this.root.render(
        <CustomAudioPlayer
          audioUrl={finalSrc}
          isCompressed={isCompressed}
          isOwner={this.isOwner}
          blockDownload={this.blockFilesDownload}
          canBeCompressed={canBeCompressed}
          optimizationRate={optimizationRate}
          title={title}
        />
      );
    }
  }


  update(node: ProseMirrorNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.render();
    return true;
  }

  destroy() {
    if (this.root) this.root.unmount();
  }

  private applyDownloadBlock() {
    // Usar los valores de la extensión, no del nodo
    const shouldBlock = this.blockFilesDownload && !this.isOwner;


    // Bloquear botón derecho
    this.dom.addEventListener('contextmenu', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear arrastrar audio
    this.dom.addEventListener('dragstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear selección de texto
    this.dom.addEventListener('selectstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear copiar
    this.dom.addEventListener('copy', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Bloquear cortar
    this.dom.addEventListener('cut', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Agregar clase CSS para estilos adicionales solo si NO es owner
    if (!this.isOwner) {
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      // Si es owner, asegurar que NO tenga las clases de bloqueo
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }

  // Método para verificar el estado actual del bloqueo
  private getBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    return {
      blockFilesDownload: this.blockFilesDownload,
      isOwner: this.isOwner,
      finalBlockDownload: shouldBlock,
      shouldBlock,
      hasBlockClasses: this.dom.classList.contains('download-blocked'),
      containerHasBlockClasses: this.reactContainer.classList.contains('download-blocked')
    };
  }

  // Método para actualizar dinámicamente el estado del bloqueo
  private updateBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    if (shouldBlock) {
      // Aplicar bloqueo
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      // Remover bloqueo
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }
}

export class CustomAudioExtension extends NodeExtension {

  constructor(
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    super();
  }


  get name() {
    return "customAudio" as const;
  }

  override createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      inline: false,
      group: 'block',
      atom: true,
      selectable: true,
      draggable: false,
      attrs: {
        src: { default: '' },
        title: { default: null },
        filename: { default: null },
        blockFilesDownload: { default: true },
        isOwner: { default: false },
        isCompressed: { default: true },
        canBeCompressed: { default: false },
        optimizationRate: { default: null },
        id: { default: null },
        "data-uploading": { default: null },
        "data-progress": { default: null },
        media_file_id: { default: null },
        ...extra.defaults(),
      },
      parseDOM: [{
        tag: 'div[data-audio-src]',
        getAttrs: (dom: HTMLElement) => ({
          src: dom.getAttribute('data-audio-src') || '',
          title: dom.getAttribute('data-audio-title'),
          blockFilesDownload: dom.getAttribute('data-block-files-download') === 'true',
          isOwner: dom.getAttribute('data-is-owner') === 'true',
          isCompressed: dom.getAttribute('data-is-compressed') !== 'false',
          canBeCompressed: dom.getAttribute('data-can-be-compressed') === 'true',
          optimizationRate: parseInt(dom.getAttribute('data-optimization-rate') || '') || null,
          id: dom.getAttribute("data-id"),
          "data-uploading": dom.getAttribute("data-uploading"),
          "data-progress": dom.getAttribute("data-progress"),
          media_file_id: dom.getAttribute('media_file_id') || null,
          ...extra.parse(dom),
        }),
      }],
      toDOM: (node) => {
        const {
          src,
          title,
          blockFilesDownload,
          isOwner,
          isCompressed,
          canBeCompressed,
          optimizationRate,
          id,
          filename,
          uploading,
          progress
        } = node.attrs;

        const attrs: Record<string, string> = {
          class: 'remirror-audio-wrapper',
          'data-audio-src': src,
          'data-block-files-download': String(blockFilesDownload),
          'data-is-owner': String(isOwner),
          'data-audio-filename': filename,
          'data-is-compressed': String(isCompressed),
          'data-can-be-compressed': String(canBeCompressed),
          "data-id": id ?? "",
          "data-uploading": uploading ?? "",
          "data-progress": progress ?? "",
        };

        if (title) attrs['data-audio-title'] = title;
        if (optimizationRate !== null) attrs['data-optimization-rate'] = String(optimizationRate);

        return [
          'div',
          {
            ...attrs,
            ...extra.dom(node),
          },
        ] as any;
      },
    };
  }



  createCommands() {
    return {
      insertAudio: (options: {
        src: string;
        title?: string;
        filename?: string;
        isCompressed?: boolean;
        isOwner?: boolean;
        blockFilesDownload?: boolean;
        canBeCompressed?: boolean;
        optimizationRate?: number;
        id?: string;
        "data-uploading"?: boolean | string | number;
        "data-progress"?: number | string;
      }): CommandFunction => {
        return ({ tr, dispatch }) => {
          const nodeType = this.type;
          const node = nodeType.create({
            src: options.src,
            filename: options.filename || null,
            title: options.title || null,
            isCompressed: false,
            isOwner: false, //harcodeamos esto por ahora dado que es la preview del editor
            blockFilesDownload: options.blockFilesDownload ?? true,
            canBeCompressed: false, //harcodeamos esto por ahora dado que es la preview del editor
            optimizationRate: null, //harcodeamos esto por ahora dado que es la preview del editor
            id: options.id ?? null,
            "data-uploading": options["data-uploading"] ?? null,
            "data-progress": options["data-progress"] ?? null,
          });
          const transaction = tr.replaceSelectionWith(node).scrollIntoView();
          if (dispatch) dispatch(transaction);
          return true;
        };
      },
    };
  }

  createNodeViews() {
    return (node, view, getPos) => {
      if (!view.editable) {
        return new PreviewAudioNodeView(node, view, this.mediaFilesMap, this.isOwner, this.blockFilesDownload);
      }
      return new AudioNodeView(node, view, getPos!, this.isOwner, this.blockFilesDownload);
    };
  }
}
